import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/context/LanguageContext';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import WhatsAppButton from '@/components/layout/WhatsAppButton';
import Hero from '@/components/sections/Hero';
import TestimonialsSection from '@/components/sections/TestimonialsSection';
import FAQSection from '@/components/sections/FAQSection';
import ContactForm from '@/components/sections/ContactForm';
import { SERVICES_FR, SERVICES_EN, CITIES, SITE_CONFIG } from '@/data/siteData';
import { Check, MapPin, ArrowRight, Phone } from 'lucide-react';

const ServicePage = () => {
  const { slug } = useParams();
  const { lang, t } = useLanguage();
  
  const services = lang === 'en' ? SERVICES_EN : SERVICES_FR;
  const service = services.find(s => s.slug === slug);
  const prefix = lang === 'en' ? '/en' : '';

  if (!service) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-slate-800 mb-4">
              {t('Service non trouvé', 'Service not found')}
            </h1>
            <Link to={`${prefix}/`} className="text-amber-600 hover:text-amber-700">
              {t("Retour à l'accueil", 'Back to home')}
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const title = lang === 'en'
    ? `${service.title} - French Riviera | VS Riviera Conciergerie`
    : `${service.title} Côte d'Azur | VS Riviera Conciergerie`;

  const description = lang === 'en'
    ? `Professional ${service.title.toLowerCase()} services on the French Riviera. Saint-Raphaël, Fréjus, Cannes, Sainte-Maxime. Quality service guaranteed.`
    : `Service professionnel de ${service.title.toLowerCase()} sur la Côte d'Azur. Saint-Raphaël, Fréjus, Cannes, Sainte-Maxime. Qualité garantie.`;

  return (
    <>
      <Helmet>
        <html lang={lang} />
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <link rel="canonical" href={`${window.location.origin}${prefix}/services/${slug}`} />
      </Helmet>

      <Header />
      
      <main data-testid={`service-page-${slug}`}>
        <Hero 
          subtitle={t('Service Premium', 'Premium Service')}
          title={service.title}
          description={service.longDescription}
        />

        {/* Service Details */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Left - Features */}
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-8">
                  {t('Ce que nous proposons', 'What we offer')}
                </h2>
                <div className="space-y-4">
                  {service.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                      <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{feature}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="mt-8 p-6 bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl text-white">
                  <h3 className="font-bold text-lg mb-2">
                    {t('Besoin de ce service ?', 'Need this service?')}
                  </h3>
                  <p className="text-amber-100 mb-4">
                    {t('Contactez-nous pour un devis gratuit', 'Contact us for a free quote')}
                  </p>
                  <a 
                    href={SITE_CONFIG.phoneLink}
                    className="inline-flex items-center gap-2 bg-white text-amber-600 px-6 py-3 rounded-full font-semibold hover:bg-amber-50 transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    {SITE_CONFIG.phone}
                  </a>
                </div>
              </div>

              {/* Right - Cities */}
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-8">
                  {t('Zones desservies', 'Service areas')}
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  {CITIES.map((city) => {
                    const cityServiceLink = lang === 'en'
                      ? `${prefix}/${service.slug}-${city.slug}`
                      : `/${services.find(s => s.id === service.id)?.slug || service.slug}-${city.slug}`;
                    
                    return (
                      <Link
                        key={city.id}
                        to={cityServiceLink}
                        className="group flex items-center gap-3 p-4 bg-slate-50 hover:bg-amber-50 rounded-xl transition-colors"
                      >
                        <MapPin className="w-5 h-5 text-slate-400 group-hover:text-amber-600" />
                        <span className="font-medium text-slate-700 group-hover:text-amber-700">
                          {lang === 'en' ? city.nameEn : city.nameFr}
                        </span>
                        <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-amber-600 ml-auto" />
                      </Link>
                    );
                  })}
                </div>

                {/* Other Services */}
                <div className="mt-8">
                  <h3 className="text-lg font-bold text-slate-800 mb-4">
                    {t('Autres services', 'Other services')}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {services.filter(s => s.id !== service.id).map((s) => (
                      <Link
                        key={s.id}
                        to={`${prefix}/services/${s.slug}`}
                        className="px-4 py-2 bg-slate-100 hover:bg-amber-100 text-slate-600 hover:text-amber-700 rounded-full text-sm transition-colors"
                      >
                        {s.title}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <TestimonialsSection />
        <ContactForm />
        <FAQSection />
      </main>

      <Footer />
      <WhatsAppButton />
    </>
  );
};

export default ServicePage;
